import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Badge from '../components/Badge';
import InvoicePrint from '../components/invoices/InvoicePrint';
import RecordSalesDialog from '../components/invoices/RecordSalesDialog';
import MarkPaidDialog from '../components/invoices/MarkPaidDialog';
import CloseOutDialog from '../components/invoices/CloseOutDialog';
import {
  INVOICE_STATUSES,
  INVOICE_STATUS_TONE,
  applyStockDeltas,
  invoiceNetTotal,
  listInvoices,
  markInvoicePaid,
  statusChangeKind,
  unmarkInvoicePaid,
  updateInvoice,
} from '../lib/invoices';
import {
  STOCKIST_STATUS_TONE,
  cancelStockistInvoice,
  deleteStatement,
  formatMonth,
  listStatements,
  markStatementPaid,
  recordMonthlySales,
  recordReturns,
  sellThrough,
  statementTotal,
  statementUnits,
  statusOfStockist,
  totalsOf,
  unmarkStatementPaid,
  unpaidDue,
} from '../lib/stockist';
import { formatJMD } from '../lib/finances';
import { listProducts } from '../lib/products';
import { useAuth } from '../context/AuthContext';
import './Invoices.css';
import { useConfirm } from '../components/ConfirmDialog';

// One invoice, in full.
//
// For a stockist consignment this is the working screen: what's still out
// there per product, and the month-by-month record of what sold and what was
// paid. For a direct invoice it's the printable sheet — that one has nothing
// ongoing to show.
export default function InvoiceDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState(null);
  const [statements, setStatements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, statement}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invoices, productRows] = await Promise.all([listInvoices(), listProducts()]);
      const found = invoices.find((inv) => inv.id === id);
      setInvoice(found || null);
      setProducts(productRows);
      if (found?.kind === 'Stockist') {
        const byInvoice = await listStatements([found.id]);
        setStatements(byInvoice[found.id] || []);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="po-loading">Loading…</p>;
  if (error) return <p className="po-error">{error}</p>;
  if (!invoice) return <p className="po-empty">That invoice no longer exists.</p>;

  // ---- direct invoices: the printable sheet, plus getting it paid ----
  if (invoice.kind !== 'Stockist') {
    const paid = invoice.status === 'Paid';
    // Net of any discount — this is what the Mark paid dialog offers to put
    // in the books, and it has to be the figure the customer actually paid.
    const amount = invoiceNetTotal(invoice);

    const handleStatus = async (next) => {
      const kind = statusChangeKind(invoice.status, next);
      if (kind === 'none') return;
      if (kind === 'pay') {
        setDialog({ type: 'pay-direct', next });
        return;
      }
      if (kind === 'unpay') {
        const result = await unmarkInvoicePaid(invoice, products, next);
        setNotice(
          result.removedIncome
            ? 'Its entry was removed from Accounting → Income, and stock put back.'
            : 'Nothing was recorded in Accounting for this one.'
        );
      } else {
        await updateInvoice(invoice.id, { status: next });
      }
      await load();
    };

    return (
      <>
        <div className="invoice-preview-bar">
          <button className="ghost-button" onClick={() => navigate('/invoices')}>
            ← Back to invoices
          </button>
          <div className="invoice-preview-actions">
            <Badge tone={INVOICE_STATUS_TONE[invoice.status]}>{invoice.status}</Badge>
            <select
              value={invoice.status}
              onChange={(e) => handleStatus(e.target.value)}
              aria-label="Invoice status"
              className="invoice-status-select"
            >
              {INVOICE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button className="ghost-button" onClick={() => navigate(`/invoices/${invoice.id}/edit`)}>
              Edit
            </button>
            <button className="ghost-button" onClick={() => window.print()}>
              Print / Save as PDF
            </button>
            {/* The main thing you come back to a sent invoice to do, so it's
                a button and not buried in the status dropdown. */}
            {!paid && (
              <button
                className="primary-action"
                onClick={() => setDialog({ type: 'pay-direct', next: 'Paid' })}
              >
                Mark paid
              </button>
            )}
          </div>
        </div>

        {notice && (
          <div className="invoice-receipt">
            <div>{notice}</div>
            <button className="ghost-button" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        )}

        <InvoicePrint invoice={invoice} />

        {dialog?.type === 'pay-direct' && (
          <MarkPaidDialog
            subtitle={`Invoice ${invoice.invoice_number} · ${invoice.bill_to_name}`}
            amount={amount}
            defaultMethod={invoice.payment_method}
            note="It also comes off your product stock."
            onSubmit={async (details) => {
              const result = await markInvoicePaid(invoice, products, details);
              setDialog(null);
              setNotice(
                (result.incomeEntry
                  ? `${formatJMD(amount)} added to Accounting → Income.`
                  : 'Already recorded in Accounting — not added again.') +
                  (result.adjustments.length
                    ? ` Stock updated: ${result.adjustments
                        .map((a) => `${a.name} ${a.from} → ${a.to}`)
                        .join(', ')}.`
                    : '') +
                  (result.stockError ? ` (Stock didn't update: ${result.stockError})` : '')
              );
              await load();
            }}
            onCancel={() => setDialog(null)}
          />
        )}
      </>
    );
  }

  // ---- stockist consignments ----
  const rows = sellThrough(invoice, statements);
  const totals = totalsOf(rows);
  const status = statusOfStockist(invoice, statements);
  const due = unpaidDue(statements);

  const handleRecordSales = async (month, lines) => {
    await recordMonthlySales(invoice.id, month, lines, user.id);
    setDialog(null);
    setNotice(`${formatMonth(month)} sales recorded.`);
    await load();
  };

  const handleMarkPaid = async (statement, details) => {
    await markStatementPaid(invoice, statement, details);
    setDialog(null);
    setNotice(
      `${formatJMD(statementTotal(statement))} added to Accounting → Income for ${formatMonth(
        statement.period_month
      )}.`
    );
    await load();
  };

  const handleUnpay = async (statement) => {
    await unmarkStatementPaid(statement);
    setNotice(
      `${formatMonth(statement.period_month)} reopened — its entry was removed from Accounting.`
    );
    await load();
  };

  const handleReturns = async (returnsByItem, deltas) => {
    await recordReturns(returnsByItem);
    // Returned units come back onto your own shelf, because they physically
    // did. Contained like every other stock write here: a count that failed
    // to update must not lose the record of the return.
    let stockNote = '';
    try {
      const applied = await applyStockDeltas(deltas, products);
      if (applied.length) {
        stockNote = ` Stock updated: ${applied.map((a) => `${a.name} ${a.from} → ${a.to}`).join(', ')}.`;
      }
    } catch (err) {
      stockNote = ` (Returns saved, but the stock count didn't update: ${err.message})`;
    }
    setDialog(null);
    setNotice(`Returns recorded.${stockNote}`);
    await load();
  };

  return (
    <>
      <div className="invoice-detail-bar">
        <button className="ghost-button" onClick={() => navigate('/invoices')}>
          ← Back to invoices
        </button>
        <div className="invoice-preview-actions">
          {status !== 'Cancelled' && (
            <button
              className="ghost-button"
              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
            >
              Edit stock sent
            </button>
          )}
          {status !== 'Completed' && status !== 'Cancelled' && (
            <>
              <button className="ghost-button" onClick={() => setDialog({ type: 'closeout' })}>
                Record returns
              </button>
              <button className="primary-action" onClick={() => setDialog({ type: 'sales' })}>
                Record monthly sales
              </button>
            </>
          )}
        </div>
      </div>

      <header className="invoice-detail-head">
        <div>
          <h1>{invoice.bill_to_name}</h1>
          <p>
            <span className="invoice-row-number">{invoice.invoice_number}</span> · stock sent{' '}
            {invoice.issue_date}
            {invoice.stockist?.payment_terms && <> · terms: {invoice.stockist.payment_terms}</>}
          </p>
        </div>
        <Badge tone={STOCKIST_STATUS_TONE[status]}>{status}</Badge>
      </header>

      {notice && (
        <div className="invoice-receipt">
          <div>{notice}</div>
          <button className="ghost-button" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="invoice-stats">
        <div className="invoice-stat">
          <span>Still with the shop</span>
          <strong>{totals.remaining}</strong>
          <em>{formatJMD(totals.remainingValue)} of stock</em>
        </div>
        <div className="invoice-stat">
          <span>Sold to date</span>
          <strong>{totals.soldToDate}</strong>
          <em>{formatJMD(totals.soldValue)} of sales</em>
        </div>
        <div className="invoice-stat">
          <span>Payment due</span>
          <strong>{formatJMD(due)}</strong>
          <em>{due > 0 ? 'From sold stock' : 'Nothing owed'}</em>
        </div>
        <div className="invoice-stat">
          <span>Returned</span>
          <strong>{totals.returned}</strong>
          <em>of {totals.sent} sent</em>
        </div>
      </div>

      <section className="panel">
        <h3 className="panel-title">Sell-through by product</h3>
        <div className="table-scroll">
          <table className="sell-through">
            <thead>
              <tr>
                <th>Product</th>
                <th className="numeric">Qty sent</th>
                <th className="numeric">Sold to date</th>
                <th className="numeric">Returned</th>
                <th className="numeric">Remaining</th>
                <th className="numeric">Unit price</th>
                <th className="numeric">Sold value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.itemId}>
                  <td>{row.description}</td>
                  <td className="numeric">{row.sent}</td>
                  <td className="numeric">{row.soldToDate}</td>
                  <td className="numeric">{row.returned || '—'}</td>
                  <td className="numeric">
                    <strong>{row.remaining}</strong>
                  </td>
                  <td className="numeric">{formatJMD(row.unitPrice)}</td>
                  <td className="numeric">{formatJMD(row.soldValue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="numeric">{totals.sent}</td>
                <td className="numeric">{totals.soldToDate}</td>
                <td className="numeric">{totals.returned}</td>
                <td className="numeric">{totals.remaining}</td>
                <td />
                <td className="numeric">{formatJMD(totals.soldValue)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="panel">
        <h3 className="panel-title">Monthly sales &amp; payments</h3>
        {statements.length === 0 ? (
          <p className="po-empty">
            No months recorded yet. Use “Record monthly sales” once the shop reports what sold.
          </p>
        ) : (
          <ul className="statement-list">
            {statements.map((statement) => {
              const amount = statementTotal(statement);
              const paid = statement.status === 'Paid';
              return (
                <li className="statement-row" key={statement.id}>
                  <div className="statement-main">
                    <span className="statement-period">{formatMonth(statement.period_month)}</span>
                    <span className="statement-meta">
                      <span>{statementUnits(statement)} units</span>
                      <span>{formatJMD(amount)}</span>
                      {paid && statement.paid_on && <span>paid {statement.paid_on}</span>}
                      {paid && statement.payment_method && <span>{statement.payment_method}</span>}
                      {paid && statement.reference && <span>ref {statement.reference}</span>}
                    </span>
                  </div>

                  <Badge tone={paid ? 'success' : 'warning'}>
                    {paid ? 'Paid' : 'Awaiting payment'}
                  </Badge>

                  <div className="statement-actions">
                    {paid ? (
                      <button onClick={() => handleUnpay(statement)}>Undo payment</button>
                    ) : (
                      <>
                        <button
                          className="statement-pay"
                          onClick={() => setDialog({ type: 'pay', statement })}
                        >
                          Mark paid
                        </button>
                        <button onClick={() => setDialog({ type: 'sales', statement })}>Edit</button>
                        <button
                          className="danger"
                          onClick={async () => {
                            if (!(await confirm({
                              title: 'Delete this month\'s sales?',
                              body: "The units it recorded go back to being unsold, and the amount due for that month disappears.",
                            }))) return;

                            await deleteStatement(statement.id);
                            await load();
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {status !== 'Completed' && status !== 'Cancelled' && (
        <section className="panel">
          <h3 className="panel-title">Closing this out</h3>
          <p className="closeout-help">
            This consignment completes when every unit has sold or come back, and every month has
            been paid.
          </p>
          <ul className="closeout-blockers">
            {totals.remaining > 0 && (
              <li>
                {totals.remaining} {totals.remaining === 1 ? 'unit is' : 'units are'} still with the
                shop.
              </li>
            )}
            {due > 0 && <li>{formatJMD(due)} of sold stock is unpaid.</li>}
            {totals.remaining === 0 && due === 0 && <li>Nothing outstanding.</li>}
          </ul>
          <button
            className="ghost-button"
            onClick={async () => {
              await cancelStockistInvoice(invoice.id);
              await load();
            }}
          >
            Cancel this consignment
          </button>
        </section>
      )}

      {dialog?.type === 'sales' && (
        <RecordSalesDialog
          invoice={invoice}
          statements={statements}
          editing={dialog.statement}
          onSubmit={handleRecordSales}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'pay' && (
        <MarkPaidDialog
          subtitle={`${formatMonth(dialog.statement.period_month)} sales · ${invoice.bill_to_name}`}
          amount={statementTotal(dialog.statement)}
          defaultMethod={invoice.payment_method}
          note="The invoice itself stays open while stock is still with the shop."
          onSubmit={(details) => handleMarkPaid(dialog.statement, details)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'closeout' && (
        <CloseOutDialog
          invoice={invoice}
          statements={statements}
          onSubmit={handleReturns}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}
